import { useId, useRef, useState } from 'react';
import { Box, Button, Divider, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  AdminSubscriptionDetail,
  AppliedPromotion,
  OfferView,
  PromotionView,
  StatementView,
} from '@/api/generated';
import { backendCode } from '@/api/errors';
import { useCanWrite } from '@/auth/authHooks';
import { useLocale } from '@/i18n/useLocale';
import { useTenantApi } from '@/tenant/useTenant';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { moneyIn } from '@/components/moneyIn';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { RequestDeletionDialog } from '@/features/settings/RequestDeletionDialog';
import { ApplyPromotionDialog } from './ApplyPromotionDialog';
import { CancelDialog } from './CancelDialog';
import { ChangeOfferDialog } from './ChangeOfferDialog';
import { CompDialog } from './CompDialog';
import { UncompDialog } from './UncompDialog';
import { WaiveDialog } from './WaiveDialog';
import { subscriberErrorMessage } from './subscriberErrors';
import { deriveStatus, type DerivedStatus } from './subscriberStatus';

export interface SubscriberActionsProps {
  detail: AdminSubscriptionDetail;
  /** The offers the change-offer picker may list (it shows only `selectable` ones). */
  offers: OfferView[];
  promotions: PromotionView[];
  offersLoad?: { error: unknown; reload: () => void } | null;
  /** Reloads the offers after change-offer finds one closed (`409 OFFER_NOT_ACTIVE`). */
  onRefreshOffers?: (() => void) | undefined;
  promotionsLoad?: { error: unknown; reload: () => void } | null;
  onDone: (opts?: { quiet?: boolean }) => void;
}

const RETRYABLE = new Set(['FAILED', 'ATTENTION', 'ACTION_REQUIRED']);

function newestRetryable(statements: StatementView[]): StatementView | undefined {
  return [...statements]
    .sort((a, b) =>
      b.periodEnd.localeCompare(a.periodEnd),
    )
    .find((row) => RETRYABLE.has(row.chargeStatus));
}

/** The End confirm's consequence for the kind of promotion (TMMAPI-0124). */
function endPromoBody(kind: AppliedPromotion['kind'] | undefined, t: TFunction): string {
  if (kind === 'PERCENT_OFF_VARIABLE' || kind === 'AMOUNT_OFF_VARIABLE') {
    return t('subscribers.endPromoConfirm.bodyVariable');
  }
  if (kind === 'OFFER') return t('subscribers.endPromoConfirm.bodyOffer');
  return t('subscribers.endPromoConfirm.bodyGeneric');
}

function isActionable(derived: DerivedStatus | null): boolean {
  return derived !== 'canceled';
}

export function SubscriberActions({
  detail,
  offers,
  promotions,
  offersLoad = null,
  onRefreshOffers,
  promotionsLoad = null,
  onDone,
}: SubscriberActionsProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const describe = useActionError();
  const describeEndPromo = useActionError(subscriberErrorMessage);
  const api = useTenantApi();
  const locale = useLocale();
  const canWrite = useCanWrite();
  const sub = detail.subscription;
  const derived = sub ? deriveStatus(sub, new Date()) : null;
  const applied = sub?.appliedPromotion ?? null;
  const live = isActionable(derived);
  const retryable = newestRetryable(detail.statements);
  const [dialog, setDialog] = useState<
    | 'offer'
    | 'promo'
    | 'endPromo'
    | 'comp'
    | 'uncomp'
    | 'cancel'
    | 'waive'
    | 'deletion'
    | 'retry'
    | null
  >(null);
  // The promotion the End confirm was opened for. A refresh behind the dialog (someone else ended
  // it) must not change what the open dialog says.
  const [endTarget, setEndTarget] = useState<AppliedPromotion | null>(null);
  const endingGroupId = useId();
  // The End confirm returns focus to End promo while it stays (a cancel). A successful End removes
  // it, but only once the reload it starts lands, after the confirm has closed: so success says so
  // up front (`rowGone()`) and focus goes straight to the ending actions group. A refresh that
  // removed it behind an open confirm is caught when the confirm closes. Never `<body>` (A11Y-010,
  // TMMRSL-0071, 0072).
  const endingGroup = useRef<HTMLDivElement>(null);
  const endFocus = useMenuFocusReturn(endingGroup);

  // STAFF is read-only: the whole action bar is hidden (H §4.4).
  if (sub?.id === undefined || !canWrite) return null;

  async function handleEndPromo() {
    if (sub?.id === undefined) return;
    try {
      await api.endSubscriptionPromotion(sub.id);
    } catch (err) {
      // Someone else ended it: the dialog says so, and the detail reloads so the button and the
      // promotion line go away.
      if (backendCode(err) === 'PROMOTION_NOT_APPLIED') onDone({ quiet: true });
      throw err;
    }
    showToast(t('subscribers.toast.promoEnded'));
    // End promo goes with the reload; the confirm closes before it lands.
    endFocus.rowGone();
    onDone();
  }

  async function handleRetry() {
    if (!retryable) return;
    await api.retryStatement(retryable.id);
    showToast(t('subscribers.toast.retried'));
    onDone();
  }

  function retryBody(statement: StatementView): string {
    const statementCurrency = statement.currency;
    // A blank currency: the no-amount copy — never a dash inside the sentence.
    if (!statementCurrency) {
      return t('subscribers.retryConfirm.bodyNoAmount');
    }
    const amount = moneyIn(
      statement.totalMinor,
      statementCurrency,
      locale,
      t('subscribers.unavailable'),
    );
    return t('subscribers.retryConfirm.body', { amount });
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        sx={{ mt: 2, alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button disabled={!live} onClick={() => setDialog('offer')}>
            {t('subscribers.actions.changeOffer')}
          </Button>
          {/* One element tree whatever the state, so a quiet refresh that applies a promotion
              doesn't replace the opener its dialog returns focus to (A11Y-010, TMMRSL-0071). The
              reason shows only while End promo is enabled, i.e. the subscription is live; a
              canceled one is disabled like its other actions, with no contradictory tooltip. */}
          <Tooltip title={live && applied ? t('subscribers.applyPromo.oneAtATime') : ''}>
            <span>
              <Button
                disabled={!live}
                // Blocked by an applied promotion, not natively disabled: a disabled button can't
                // hold focus, and this one must keep it (and its tooltip) for a keyboard user.
                {...(live && applied
                  ? { 'aria-disabled': true, className: 'Mui-disabled' }
                  : { onClick: () => setDialog('promo') })}
              >
                {t('subscribers.actions.applyPromo')}
              </Button>
            </span>
          </Tooltip>
          {derived === 'comped' ? (
            <Button disabled={!live} onClick={() => setDialog('uncomp')}>
              {t('subscribers.actions.removeComp')}
            </Button>
          ) : (
            <Button disabled={!live} onClick={() => setDialog('comp')}>
              {t('subscribers.actions.comp')}
            </Button>
          )}
          {retryable ? (
            <>
              <Button onClick={() => setDialog('retry')}>{t('subscribers.actions.retry')}</Button>
              <Button onClick={() => setDialog('waive')}>{t('subscribers.actions.waive')}</Button>
            </>
          ) : (
            <Tooltip title={t('subscribers.noRetryable')}>
              <span>
                {/* Blocked, not natively disabled: it keeps focus, so the keyboard reaches its
                    reason; with no handler, Enter and click do nothing (TMMRSL-0072). */}
                <Button aria-disabled className="Mui-disabled">
                  {t('subscribers.actions.retry')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        {/* The ending actions are set apart from the everyday ones (ACT-005). */}
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Stack
          direction="row"
          spacing={1}
          role="group"
          aria-labelledby={endingGroupId}
          tabIndex={-1}
          ref={endingGroup}
          // The focus fallback shows the theme's focus ring on keyboard focus (A11Y-002), as the
          // heading fallbacks do; only a pointer-driven focus hides it.
          sx={{ flexWrap: 'wrap', rowGap: 1, '&:focus:not(:focus-visible)': { outline: 'none' } }}
        >
          {/* Ending a promotion is reversible (the code can be applied again), so it isn't error-coloured. */}
          {applied ? (
            <Button
              disabled={!live}
              onClick={(event) => {
                endFocus.remember(event.currentTarget);
                setEndTarget(applied);
                setDialog('endPromo');
              }}
            >
              {t('subscribers.actions.endPromo')}
            </Button>
          ) : null}
          <Button color="error" disabled={!live} onClick={() => setDialog('cancel')}>
            {t('subscribers.actions.cancel')}
          </Button>
          <Button color="error" onClick={() => setDialog('deletion')}>
            {t('subscribers.actions.requestDeletion')}
          </Button>
          <Box component="span" id={endingGroupId} sx={visuallyHidden}>
            {t('subscribers.actions.endingGroup')}
          </Box>
        </Stack>
      </Stack>
      <ConfirmDialog
        open={dialog === 'endPromo'}
        title={t('subscribers.endPromoConfirm.title', {
          code: endTarget?.code ?? t('subscribers.unavailable'),
          email: sub.email,
        })}
        consequence={endPromoBody(endTarget?.kind, t)}
        confirmLabel={t('subscribers.endPromoConfirm.confirm')}
        onConfirm={handleEndPromo}
        onCancel={() => {
          setDialog(null);
          endFocus.dialogClosed();
        }}
        errorMessage={(err) => describeEndPromo(err).message}
      />
      <ChangeOfferDialog
        open={dialog === 'offer'}
        onClose={() => setDialog(null)}
        onDone={onDone}
        subscriptionId={sub.id}
        currentInterval={sub.interval}
        offers={offers}
        {...(offersLoad ? { loadError: offersLoad.error, onRetryLoad: offersLoad.reload } : {})}
        onRefreshOffers={onRefreshOffers}
      />
      <ApplyPromotionDialog
        open={dialog === 'promo'}
        onClose={() => setDialog(null)}
        onDone={onDone}
        subscriptionId={sub.id}
        promotions={promotions}
        {...(promotionsLoad
          ? { loadError: promotionsLoad.error, onRetryLoad: promotionsLoad.reload }
          : {})}
      />
      <CompDialog
        open={dialog === 'comp'}
        onClose={() => setDialog(null)}
        onDone={onDone}
        subscriptionId={sub.id}
      />
      <UncompDialog
        open={dialog === 'uncomp'}
        onClose={() => setDialog(null)}
        onDone={onDone}
        subscriptionId={sub.id}
      />
      <CancelDialog
        open={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        onDone={onDone}
        subscriptionId={sub.id}
        email={sub.email}
        currentPeriodEnd={sub.currentPeriodEnd}
      />
      {retryable ? (
        <ConfirmDialog
          open={dialog === 'retry'}
          title={t('subscribers.retryConfirm.title', {
            email: sub.email,
          })}
          consequence={retryBody(retryable)}
          confirmLabel={t('subscribers.retryConfirm.confirm')}
          onConfirm={handleRetry}
          onCancel={() => setDialog(null)}
          errorMessage={(err) => describe(err).message}
        />
      ) : null}
      {retryable ? (
        <WaiveDialog
          open={dialog === 'waive'}
          onClose={() => setDialog(null)}
          onDone={onDone}
          statementId={retryable.id}
        />
      ) : null}
      <RequestDeletionDialog
        open={dialog === 'deletion'}
        onClose={() => setDialog(null)}
        prefill={{
          userId: sub.userId,
          ...(sub.displayName !== undefined ? { displayName: sub.displayName } : {}),
          email: sub.email,
        }}
        onSubmit={async (body) => {
          await api.requestDeletion(body);
          showToast(t('settings.data.toast.requested'));
          onDone();
        }}
      />
    </Box>
  );
}
